import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { cloneDeep, findIndex, has, each, isEqual } from 'lodash';

import { COMPANIES_PATHS, Company } from '../companies';
import { CompaniesService } from '../companies.service';
import { environment } from '../../../../environments/environment.prod';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';

interface CompaniesViewStateParamsInterface {
  campaign_id: string | number;
  campaign_contract: string | number;
  partner_code: string | number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  standalone: false,
})
export class CompaniesComponent implements OnInit {
  public COMPANIES_PATHS = COMPANIES_PATHS;
  public dataLoading: boolean;
  public companiesLoading: boolean;
  public contractNumber: number | string;
  public filterCompanies = {
    companies: null,
    codes: null,
    companySelected: null,
    companyContract: '',
    codeSelected: null,
    onApplyFilter: this.onApplyCompaniesFilter.bind(this),
    onResetFilter: this.onResetCompaniesFilter.bind(this),
    onGetCompaniesPartial: this.onGetCompaniesPartial.bind(this),
  };
  public companiesTable = { companies: [], offset: 0, limit: 25, count: 0 };
  public companies: Company[];
  public stateParams: CompaniesViewStateParamsInterface = {
    campaign_id: '',
    campaign_contract: '',
    partner_code: '',
  };
  public stateParamsDefault: CompaniesViewStateParamsInterface;
  public companiesBreadcrumbs = [{ text: 'Компании', url: null }];

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private breadcrumbsService: BreadcrumbsService,
    public companyService: CompaniesService
  ) {}

  ngOnInit() {
    this.stateParamsDefault = cloneDeep(this.stateParams);

    this.route.params.subscribe((params) => {
      const stateParams = this.stateParams;
      let countParameters = 0;
      let countExistsParameters = 0;

      stateParams.campaign_id = params['campaign_id'] || '';
      stateParams.campaign_contract = params['campaign_contract'] || '';
      stateParams.partner_code = params['partner_code'] || '';

      this.contractNumber = this.stateParams.campaign_contract;

      each(stateParams, function (value, key) {
        countParameters++;

        if (has(params, [key])) {
          countExistsParameters++;
        }
      });

      if (countParameters === countExistsParameters) {
        this.makeExportLink();
        this.getCompaniesFilter();
      } else {
        this.router.navigate([COMPANIES_PATHS.companies, stateParams], {
          replaceUrl: true,
        });
      }
    });

    this.breadcrumbsService.updateBreadcrumbs(this.companiesBreadcrumbs);
  }

  public makeExportLink(): string {
    let exportLink = environment.apiPrefix + '/admin/campaigns/download?';
    const codeSelected = this.filterCompanies.codeSelected;
    const stateParams = cloneDeep(this.stateParams);
    const params = [];

    if (!codeSelected) {
      stateParams['partner_code'] = '';
      stateParams['has_partner_code'] = '';
    }

    if (codeSelected && !codeSelected['is_without_code']) {
      stateParams['partner_code'] = codeSelected['partner_code'];
      stateParams['has_partner_code'] = true;
    }

    if (codeSelected && codeSelected['is_without_code']) {
      stateParams['partner_code'] = '';
      stateParams['has_partner_code'] = false;
    }

    each(stateParams, (value, key) => params.push(key + '=' + value));

    return (exportLink += params.join('&'));
  }

  public exportCompanies(): void {
    let link = this.makeExportLink();
    let a = document.createElement('a');
    a.href = link;
    a.target = '_self';
    a.download = link.split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public companyCreate(): void {
    this.router.navigate([COMPANIES_PATHS.companies_create]);
  }

  private getCompaniesFilter(): void {
    if (this.companies) {
      this.getCompanies();
      return;
    }

    this.dataLoading = true;
    this.companyService.getCompaniesFilter().subscribe(
      (response) => {
        this.filterCompanies.companies = response['data'].map((company) => {
          const companyNewName = company.ownership_type.ot_short + ' "' + company.ps_name + '"';
          if (company.is_visible) {
            company.new_name = companyNewName;
          } else {
            company.new_name = '<span class="app-combobox__item--highlight-part">' + companyNewName + '</span>';
          }
          return company;
        });

        if (this.stateParams.campaign_id) {
          let companySelectedIndex = findIndex(this.filterCompanies.companies, {
            campaign_id: +this.stateParams.campaign_id,
          });

          if (companySelectedIndex > -1) {
            this.filterCompanies.companySelected = this.filterCompanies.companies[companySelectedIndex];
          } else {
            this.filterCompanies.companySelected = null;
          }
        } else {
          this.filterCompanies.companySelected = null;
        }

        this.getCodesFilter();
      },
      (error) => {
        this.filterCompanies.companies = null;
        this.dataLoading = false;
      }
    );
  }

  private getCodesFilter(): void {
    this.companyService.getCodesFilter().subscribe(
      (response) => {
        this.filterCompanies.codes = response['data'].map((c) => {
          c.is_without_code = false;
          return c;
        });
        this.filterCompanies.codes.unshift({
          partner_code: 'Компании без кода',
          is_without_code: true,
        });

        if (this.stateParams.partner_code) {
          let codeSelectedIndex = findIndex(this.filterCompanies.codes, {
            partner_code: this.stateParams.partner_code,
          });

          if (codeSelectedIndex > -1) {
            this.filterCompanies.codeSelected = this.filterCompanies.codes[codeSelectedIndex];
          } else {
            this.filterCompanies.codeSelected = null;
          }
        } else {
          this.filterCompanies.codeSelected = null;
        }

        this.getCompanies();
      },
      (error) => {
        this.filterCompanies.codes = null;
        this.dataLoading = false;
      }
    );
  }

  private getCompanies(): void {
    const codeSelected = this.filterCompanies.codeSelected;
    const stateParams = cloneDeep(this.stateParams);
    stateParams['offset'] = this.companiesTable.offset;
    stateParams['limit'] = this.companiesTable.limit;

    if (!codeSelected) {
      stateParams['has_partner_code'] = '';
    }

    if (codeSelected && !codeSelected['is_without_code']) {
      stateParams['has_partner_code'] = true;
    }

    if (codeSelected && codeSelected['is_without_code']) {
      stateParams['partner_code'] = '';
      stateParams['has_partner_code'] = false;
    }

    this.dataLoading = true;
    this.companyService.getCompanies(stateParams).subscribe(
      (response) => {
        const companies = response['data']['results'].map((company) => {
          company.new_name = company.ownership_type.ot_short + ' "' + company.ps_name + '"';
          return company;
        });
        this.companiesTable.count = response['data']['count'];

        if (this.companiesTable.offset > 0) {
          this.companiesTable.companies = this.companiesTable.companies.concat(companies);
        } else {
          this.companiesTable.companies = companies;
        }

        this.dataLoading = false;
      },
      (error) => {
        console.log(error);
        this.companiesTable.companies = [];
        this.dataLoading = false;
      }
    );
  }

  public onApplyCompaniesFilter(): void {
    const filterState = this.filterCompanies;
    const stateParams = this.stateParams;

    this.filterCompanies.companySelected = filterState.companySelected;
    this.companiesTable.offset = 0;

    stateParams.campaign_id = (this.filterCompanies.companySelected && this.filterCompanies.companySelected.campaign_id) || '';
    stateParams.partner_code = (this.filterCompanies.codeSelected && this.filterCompanies.codeSelected.partner_code) || '';
    stateParams.campaign_contract = filterState.companyContract || '';

    this.router.navigate([COMPANIES_PATHS.companies, stateParams]);
  }

  public onResetCompaniesFilter(): void {
    const filter = this.filterCompanies;

    filter.companySelected = null;
    filter.codeSelected = null;
    filter.companyContract = '';

    this.companiesTable.offset = 0;

    if (isEqual(this.stateParamsDefault, this.stateParams)) {
      this.getCompanies();
    } else {
      this.router.navigate([COMPANIES_PATHS.companies, cloneDeep(this.stateParamsDefault)]);
    }
  }

  public onGetCompaniesPartial(): void {
    this.companiesTable.offset += this.companiesTable.limit;
    this.getCompanies();
  }
}
